import { Edit3, Play } from 'lucide-react';
import { isUsMarketHoursUtc } from 'shared';

import type { PositionIndicatorHistoryState } from '../../features/positions/queries';
import type { PositionDto } from '../../lib/api/endpoints';
import { formatCurrency } from '../../lib/format/currency';
import { relativeTimestamp } from '../../lib/format/date';
import { positionReplayHref } from '../../lib/navigation/positionReplay';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';

import { AtrExtensionValue } from './AtrExtensionValue';
import { EditStopDialog } from './EditStopDialog';
import { PositionSideBadge } from './PositionSideBadge';
import { PositionStatusPills } from './PositionStatusPills';
import { RebuyHigherBadge } from './RebuyHigherBadge';

interface PositionCardProps {
  position: PositionDto;
  indicatorHistory: PositionIndicatorHistoryState | undefined;
  onOpenDetail: (symbol: string) => void;
  onEditPosition: (position: PositionDto) => void;
}

function percent(value: string | null): string {
  if (value === null) return '-';
  return `${(Number(value) * 100).toFixed(1)}%`;
}

function distanceTone(value: string | null): 'danger' | 'neutral' | 'success' | 'warning' {
  if (value === null) return 'neutral';
  const distance = Number(value);
  if (distance > 0.05) return 'success';
  if (distance >= 0.02) return 'warning';
  return 'danger';
}

function pnlTone(value: string | null): 'danger' | 'neutral' | 'success' {
  if (value === null || Number(value) === 0) return 'neutral';
  return Number(value) > 0 ? 'success' : 'danger';
}

function isStaleQuote(lastQuoteAt: string | null): boolean {
  if (!lastQuoteAt) return false;
  const quotedAt = new Date(lastQuoteAt).getTime();
  if (!Number.isFinite(quotedAt)) return false;
  return Date.now() - quotedAt > 30 * 60_000 && isUsMarketHoursUtc(new Date());
}

function priceSourceLabel(source: PositionDto['priceSource']): string {
  if (source === 'last-fill') return 'last fill';
  return source;
}

export function PositionCard({
  onEditPosition,
  onOpenDetail,
  position,
  indicatorHistory,
}: PositionCardProps) {
  const priceTimestamp = position.priceFetchedAt ?? position.lastQuoteAt;
  const stalePrice =
    position.priceStale || (priceTimestamp !== null && isStaleQuote(priceTimestamp));
  const invalidStop = position.statusFlags.includes('INVALID_STOP');
  const valuationUnavailable = position.shortProvenance === 'AMBIGUOUS_PREHISTORY';

  return (
    <article className="rounded border border-border bg-surface p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <button
            type="button"
            className="text-left disabled:cursor-default"
            disabled={valuationUnavailable}
            onClick={() => onOpenDetail(position.symbol)}
          >
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold">{position.symbol}</h3>
              <PositionSideBadge shortProvenance={position.shortProvenance} side={position.side} />
            </div>
            <p className="text-sm text-text-muted">
              {position.qty} shares | {position.theme ?? 'No theme'}
            </p>
          </button>
          {position.rebuyHigher ? (
            <RebuyHigherBadge
              className="mt-2"
              currency={position.currency}
              flag={position.rebuyHigher}
            />
          ) : null}
        </div>
        <div className="flex gap-2">
          <Button asChild size="sm" variant="ghost">
            <a
              aria-label={`Replay trades for ${position.symbol}`}
              href={positionReplayHref(position.symbol, position.accountId)}
            >
              <Play className="h-4 w-4" aria-hidden="true" />
            </a>
          </Button>
          <Button
            aria-label={`Edit position override for ${position.symbol}`}
            size="sm"
            type="button"
            variant="ghost"
            onClick={() => onEditPosition(position)}
          >
            <Edit3 className="h-4 w-4" aria-hidden="true" />
          </Button>
          <EditStopDialog
            position={position}
            trigger={
              <Button aria-label={`Edit stop for ${position.symbol}`} size="sm" variant="secondary">
                <Edit3 className="h-4 w-4" aria-hidden="true" />
              </Button>
            }
          />
        </div>
      </div>
      <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt className="text-text-muted">Last</dt>
          <dd className="tabular-nums">
            {valuationUnavailable ? (
              <span className="text-warn">Valuation unavailable</span>
            ) : (
              <>
                <span>{formatCurrency(position.currentPrice, position.currency)}</span>
                <span
                  className={cn(
                    'block text-xs',
                    stalePrice || position.priceSource === 'last-fill'
                      ? 'text-warn'
                      : 'text-text-muted',
                  )}
                >
                  {priceSourceLabel(position.priceSource)}
                  {stalePrice ? ' stale' : ''}
                </span>
                {priceTimestamp ? (
                  <span className={cn('block text-xs text-text-muted', stalePrice && 'text-warn')}>
                    {relativeTimestamp(priceTimestamp)}
                  </span>
                ) : null}
              </>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-text-muted">% NAV</dt>
          <dd className="tabular-nums">{valuationUnavailable ? '—' : percent(position.pctNav)}</dd>
        </div>
        <div>
          <dt className="text-text-muted">Stop</dt>
          <dd className="tabular-nums">
            {position.currentStop ? formatCurrency(position.currentStop, position.currency) : '-'}
          </dd>
        </div>
        <div>
          <dt className="text-text-muted">$ Risk</dt>
          <dd>
            {invalidStop ? (
              <Badge tone="danger">Invalid</Badge>
            ) : (
              <span className="tabular-nums">
                {position.dollarRiskToStop
                  ? formatCurrency(position.dollarRiskToStop, position.currency)
                  : '-'}
              </span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-text-muted">Distance</dt>
          <dd>
            <Badge tone={invalidStop ? 'danger' : distanceTone(position.distanceToStop)}>
              {invalidStop ? 'Invalid' : percent(position.distanceToStop)}
            </Badge>
          </dd>
        </div>
        <div>
          <dt className="text-text-muted">Unreal. P&amp;L</dt>
          <dd>
            <Badge tone={pnlTone(position.unrealizedPnlDollar)}>
              {position.unrealizedPnlDollar
                ? formatCurrency(position.unrealizedPnlDollar, position.currency)
                : '—'}
            </Badge>
          </dd>
        </div>
        <div>
          <dt className="text-text-muted">Days</dt>
          <dd className="tabular-nums">{position.daysHeld}</dd>
        </div>
        <div>
          <dt
            className="text-text-muted"
            title="Price distance from 50-day SMA, measured in ATR(14) multiples"
          >
            ATR Ext
          </dt>
          <dd>
            <AtrExtensionValue history={indicatorHistory} />
          </dd>
        </div>
      </dl>
      <div className="mt-4">
        <PositionStatusPills flags={position.statusFlags} />
      </div>
    </article>
  );
}
